import {
  ANIMATION_CONFIG,
  GRADIENT_POINTS,
  GradientPoint,
  EntryTiming,
} from './BannerAnimated.data'

/**
 * Randomized phase offsets so each point breathes at its own rhythm
 * rather than all pulsing in sync.
 */
export function buildPhaseOffsets(): number[] {
  return GRADIENT_POINTS.map(
    (_, i) => (i * Math.PI * 2) / GRADIENT_POINTS.length + Math.random() * 0.8,
  )
}

/**
 * Slightly varied entry durations for a more organic, natural feel.
 */
export function buildEntryTimings(): EntryTiming[] {
  return GRADIENT_POINTS.map((_, i) => ({
    duration: ANIMATION_CONFIG.entryDuration + i * 500 + Math.sin(i * 1.7) * 400,
    delay: i * ANIMATION_CONFIG.entryStagger,
  }))
}

/** Strong deceleration curve for point entry movement. */
export function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4)
}

interface CanvasDrawParams {
  ctx: CanvasRenderingContext2D
  canvasWidth: number
  canvasHeight: number
}

/** Clears the canvas with the base background color. */
export function drawBackground({ ctx, canvasWidth, canvasHeight }: CanvasDrawParams) {
  ctx.fillStyle = '#060B0F'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)
}

/** Subtle teal atmosphere layered on top of the gradient blobs. */
export function drawAtmosphere({ ctx, canvasWidth, canvasHeight }: CanvasDrawParams) {
  ctx.globalCompositeOperation = 'screen'
  const grad = ctx.createRadialGradient(
    canvasWidth * 0.6,
    canvasHeight * 0.3,
    0,
    canvasWidth * 0.5,
    canvasHeight * 0.5,
    Math.max(canvasWidth, canvasHeight) * 0.7,
  )
  grad.addColorStop(0, 'rgba(20,70,90,0.15)')
  grad.addColorStop(0.5, 'rgba(12,45,60,0.08)')
  grad.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)
}

/** Dark edge vignette to ground the composition. */
export function drawVignette({ ctx, canvasWidth, canvasHeight }: CanvasDrawParams) {
  ctx.globalCompositeOperation = 'source-over'
  const grad = ctx.createRadialGradient(
    canvasWidth * 0.7,
    canvasHeight * 0.25,
    canvasWidth * 0.15,
    canvasWidth * 0.5,
    canvasHeight * 0.5,
    Math.max(canvasWidth, canvasHeight) * 0.75,
  )
  grad.addColorStop(0, 'rgba(0,0,0,0)')
  grad.addColorStop(0.6, 'rgba(0,0,0,0.15)')
  grad.addColorStop(1, 'rgba(0,0,0,0.55)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)
}

interface ResolvePointPositionParams {
  pointIndex: number
  elapsed: number
  entryTimings: EntryTiming[]
  phaseOffsets: number[]
}

interface ResolvePointPositionReturn {
  cx: number
  cy: number
}

/**
 * Resolves the [cx, cy] position of a point at a given elapsed time.
 * Phases: waiting → traveling (eased) → settled + breathing.
 */
export function resolvePointPosition({
  pointIndex,
  elapsed,
  entryTimings,
  phaseOffsets,
}: ResolvePointPositionParams): ResolvePointPositionReturn {
  const point = GRADIENT_POINTS[pointIndex]
  const { duration, delay } = entryTimings[pointIndex]
  const pointElapsed = elapsed - delay

  if (pointElapsed <= 0) {
    // Still waiting — hold at the shared start position
    return { cx: ANIMATION_CONFIG.startX, cy: ANIMATION_CONFIG.startY }
  }

  if (pointElapsed < duration) {
    // Traveling to its final position
    const progress = easeOutQuart(pointElapsed / duration)
    return {
      cx: ANIMATION_CONFIG.startX + (point.finalX - ANIMATION_CONFIG.startX) * progress,
      cy: ANIMATION_CONFIG.startY + (point.finalY - ANIMATION_CONFIG.startY) * progress,
    }
  }

  // Settled — apply gentle breathing drift
  let { finalX: cx, finalY: cy } = point
  if (ANIMATION_CONFIG.breathingEnabled) {
    const breathTime = (elapsed - delay - duration) * 0.001
    const phase = phaseOffsets[pointIndex]
    cx += Math.sin(breathTime * 0.6 + phase) * ANIMATION_CONFIG.breathingRange
    cy += Math.cos(breathTime * 0.45 + phase * 1.3) * ANIMATION_CONFIG.breathingRange * 0.7
  }
  return { cx, cy }
}

interface DrawGradientBlobParams {
  ctx: CanvasRenderingContext2D
  point: GradientPoint
  centerX: number
  centerY: number
  canvasWidth: number
  canvasHeight: number
}

/** Paints a single radial gradient blob using 'screen' blend mode. */
export function drawGradientBlob({
  ctx,
  point,
  centerX,
  centerY,
  canvasWidth,
  canvasHeight,
}: DrawGradientBlobParams) {
  const px = centerX * canvasWidth
  const py = centerY * canvasHeight
  const r = point.radius * Math.max(canvasWidth, canvasHeight)
  const [cr, cg, cb] = point.color

  const grad = ctx.createRadialGradient(px, py, 0, px, py, r)
  grad.addColorStop(0, `rgba(${cr},${cg},${cb},${point.opacity})`)
  grad.addColorStop(0.2, `rgba(${cr},${cg},${cb},${point.opacity * 0.7})`)
  grad.addColorStop(0.5, `rgba(${cr},${cg},${cb},${point.opacity * 0.35})`)
  grad.addColorStop(0.8, `rgba(${cr},${cg},${cb},${point.opacity * 0.08})`)
  grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`)

  ctx.globalCompositeOperation = 'screen'
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)
}
